import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "@shared/context/AuthContext";
import { HELP_PORTAL_MODE } from "@shared/config/helpSupport";
import logger from "@shared/utils/logger";

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const TOKEN = import.meta.env.VITE_FRESHDESK_TOKEN || "";
const HOST = import.meta.env.VITE_FRESHDESK_HOST || "";
const WIDGET_ID = import.meta.env.VITE_FRESHDESK_WIDGET_ID || "";
const allowFreshchatOnLocalhost =
  import.meta.env.VITE_FRESHCHAT_ALLOW_LOCALHOST === "true";
const isLocalhostRuntime =
  typeof window !== "undefined" &&
  /(^|\.)localhost$/i.test(window.location.hostname || "");
const prefersFreshdeskPortal = HELP_PORTAL_MODE === "freshdesk";
const ENABLED =
  prefersFreshdeskPortal &&
  import.meta.env.VITE_FRESHCHAT_ENABLED !== "false" &&
  !!TOKEN &&
  !!HOST &&
  !!WIDGET_ID &&
  (!isLocalhostRuntime || allowFreshchatOnLocalhost);

/** Freshdesk webchat widget script URL. */
const WIDGET_SCRIPT_URL = `${HOST}/webchat/js/widget.js`;

// ---------------------------------------------------------------------------
// Context Types
// ---------------------------------------------------------------------------

interface FreshchatContextType {
  /** Whether the Freshworks integration is configured (env vars present). */
  enabled: boolean;
  /** Whether the widget script has loaded and initialized successfully. */
  ready: boolean;
  /** Why the widget could not be loaded or initialized, if known. */
  error: string | null;
  /** Whether the user has granted consent for support cookies (GDPR). */
  consentGranted: boolean;
  /** Grant consent — triggers widget script load. */
  grantConsent: () => void;
  /** Revoke consent — destroys widget and removes cookies. */
  revokeConsent: () => void;
  /** Open the chat/support widget. */
  open: () => void;
  /** Close the widget. */
  close: () => void;
  /** Show the floating launcher button. */
  showLauncher: () => void;
  /** Hide the floating launcher button (e.g., on login page). */
  hideLauncher: () => void;
}

const disabledFreshchatContext: FreshchatContextType = {
  enabled: false,
  ready: false,
  error: null,
  consentGranted: false,
  grantConsent: () => undefined,
  revokeConsent: () => undefined,
  open: () => undefined,
  close: () => undefined,
  showLauncher: () => undefined,
  hideLauncher: () => undefined,
};

const FreshchatContext = createContext<FreshchatContextType | undefined>(
  undefined,
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CONSENT_KEY = "freshchat_consent";

function getStoredConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === "granted";
  } catch {
    return false;
  }
}

function setStoredConsent(granted: boolean) {
  try {
    if (granted) {
      localStorage.setItem(CONSENT_KEY, "granted");
    } else {
      localStorage.removeItem(CONSENT_KEY);
    }
  } catch {
    // localStorage may be unavailable
  }
}

/** Remove all Freshchat cookies (fc_* prefix). */
function clearFreshchatCookies() {
  try {
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (name.startsWith("fc_") || name.startsWith("fw_")) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    });
  } catch {
    // non-fatal
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const FreshchatProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentGranted, setConsentGranted] = useState(getStoredConsent);
  const scriptLoadedRef = useRef(false);
  const identifiedUserRef = useRef<string | null>(null);

  // Log configuration on first mount
  useEffect(() => {
    logger.debug("[Freshchat] Provider mounted", {
      enabled: ENABLED,
      helpPortalMode: HELP_PORTAL_MODE,
      widgetIdPresent: !!WIDGET_ID,
      host: HOST || "(empty)",
      storedConsent: getStoredConsent(),
    });

    if (!prefersFreshdeskPortal) {
      logger.debug(
        "[Freshchat] Widget disabled because help portal mode is local",
      );
      return;
    }

    if (!TOKEN || !HOST || !WIDGET_ID) {
      logger.warn(
        "[Freshchat] VITE_FRESHDESK_TOKEN, VITE_FRESHDESK_HOST, or VITE_FRESHDESK_WIDGET_ID is not set — widget disabled",
      );
    }
  }, []);

  // -----------------------------------------------------------------------
  // Script loading
  // -----------------------------------------------------------------------

  const loadWidgetScript = useCallback(() => {
    if (!ENABLED || scriptLoadedRef.current) {
      return;
    }

    // Prevent double-loading
    if (document.querySelector("script[data-freshdesk-widget]")) {
      scriptLoadedRef.current = true;
      return;
    }

    logger.debug("[Freshchat] Loading widget script", {
      host: HOST,
      scriptUrl: WIDGET_SCRIPT_URL,
    });

    const script = document.createElement("script");
    script.src = WIDGET_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-freshdesk-widget", "true");

    const reportWidgetUnavailable = (
      logMessage: string,
      details?: Record<string, unknown>,
    ) => {
      logger.error(logMessage, details);
      setError("Live chat is temporarily unavailable. Please use email or phone support.");
      setReady(false);
      scriptLoadedRef.current = false;
      identifiedUserRef.current = null;
      script.remove();
    };

    script.onload = () => {
      scriptLoadedRef.current = true;
      setError(null);

      // Poll until fdWidget is available, then initialize.
      let pollCount = 0;
      const timeoutId = window.setTimeout(() => {
        clearInterval(check);
        if (!window.fdWidget) {
          reportWidgetUnavailable(
            "[Freshchat] fdWidget API not available after 10s timeout",
            { pollCount, scriptSrc: WIDGET_SCRIPT_URL },
          );
        }
      }, 10_000);

      const check = setInterval(() => {
        pollCount++;
        if (window.fdWidget) {
          clearInterval(check);
          clearTimeout(timeoutId);
          try {
            window.fdWidget.init({
              token: TOKEN,
              host: HOST,
              widgetId: WIDGET_ID,
            });
            logger.debug("[Freshchat] fdWidget.init() called successfully");
            setError(null);
            setReady(true);
          } catch (err) {
            reportWidgetUnavailable("[Freshchat] fdWidget.init() failed", {
              error: err instanceof Error ? err.message : String(err),
            });
          }
        }
      }, 200);
    };

    script.onerror = () => {
      reportWidgetUnavailable("[Freshchat] Failed to load widget script", {
        scriptUrl: WIDGET_SCRIPT_URL,
        host: HOST,
      });
    };

    document.head.appendChild(script);
  }, []);

  // Load script when consent is granted
  useEffect(() => {
    if (consentGranted && ENABLED) {
      setError(null);
      loadWidgetScript();
    }
  }, [consentGranted, loadWidgetScript]);

  // -----------------------------------------------------------------------
  // User identity sync
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!ready || !window.fdWidget) {
      return;
    }

    if (isAuthenticated && user) {
      // Only re-identify if the user changed
      if (identifiedUserRef.current === user.id) {
        return;
      }
      logger.debug("[Freshchat] Identifying user to widget", {
        userId: user.id,
        previousUser: identifiedUserRef.current,
      });
      identifiedUserRef.current = user.id;

      try {
        if (window.fdWidget.user) {
          const userApi = window.fdWidget.user as any;

          // Prefer single-call property set when available. Some widget builds
          // expose setFirstName/setLastName but rely internally on setProperties.
          if (typeof userApi.setProperties === "function") {
            userApi.setProperties({
              firstName: user.first_name || "",
              lastName: user.last_name || "",
              email: user.email || "",
            });
          } else {
            if (typeof userApi.setEmail === "function") {
              try {
                userApi.setEmail(user.email || "");
              } catch {
                // Ignore SDK shape mismatch for legacy widget versions.
              }
            }
            if (typeof userApi.setFirstName === "function") {
              try {
                userApi.setFirstName(user.first_name || "");
              } catch {
                // Ignore SDK shape mismatch for legacy widget versions.
              }
            }
            if (typeof userApi.setLastName === "function") {
              try {
                userApi.setLastName(user.last_name || "");
              } catch {
                // Ignore SDK shape mismatch for legacy widget versions.
              }
            }
          }
        }
      } catch (err) {
        logger.warn("[Freshchat] Failed to set user identity", err);
      }
    } else {
      identifiedUserRef.current = null;
    }
  }, [ready, isAuthenticated, user]);

  // Hide launcher on unauthenticated pages
  useEffect(() => {
    if (!ready || !window.fdWidget) return;

    if (isAuthenticated) {
      window.fdWidget.show();
    } else {
      window.fdWidget.hide();
    }
  }, [ready, isAuthenticated]);

  // -----------------------------------------------------------------------
  // Consent management
  // -----------------------------------------------------------------------

  const grantConsent = useCallback(() => {
    logger.debug("[Freshchat] Consent GRANTED by user");
    setStoredConsent(true);
    setError(null);
    setConsentGranted(true);
  }, []);

  const revokeConsent = useCallback(() => {
    logger.debug(
      "[Freshchat] Consent REVOKED by user — destroying widget and cleaning up",
    );
    setStoredConsent(false);
    setConsentGranted(false);
    setError(null);

    // Destroy widget and clean up
    if (window.fdWidget) {
      try {
        window.fdWidget.destroy();
      } catch (err) {
        logger.warn(
          "[Freshchat] Error destroying widget during consent revocation",
          err,
        );
      }
    }
    clearFreshchatCookies();
    scriptLoadedRef.current = false;
    setReady(false);
    identifiedUserRef.current = null;
  }, []);

  // -----------------------------------------------------------------------
  // Widget controls
  // -----------------------------------------------------------------------

  const open = useCallback(() => {
    if (ready && window.fdWidget) {
      window.fdWidget.open();
    } else {
      logger.warn("[Freshchat] open() called but widget not ready", {
        ready,
        widgetAvailable: !!window.fdWidget,
      });
    }
  }, [ready]);

  const close = useCallback(() => {
    if (ready && window.fdWidget) {
      window.fdWidget.close();
    } else {
      logger.warn("[Freshchat] close() called but widget not ready", {
        ready,
        widgetAvailable: !!window.fdWidget,
      });
    }
  }, [ready]);

  const showLauncher = useCallback(() => {
    if (ready && window.fdWidget) {
      window.fdWidget.show();
    } else {
      logger.warn("[Freshchat] showLauncher() called but widget not ready", {
        ready,
        widgetAvailable: !!window.fdWidget,
      });
    }
  }, [ready]);

  const hideLauncher = useCallback(() => {
    if (ready && window.fdWidget) {
      window.fdWidget.hide();
    } else {
      logger.warn("[Freshchat] hideLauncher() called but widget not ready", {
        ready,
        widgetAvailable: !!window.fdWidget,
      });
    }
  }, [ready]);

  // -----------------------------------------------------------------------
  // Cleanup on unmount
  // -----------------------------------------------------------------------

  useEffect(() => {
    return () => {
      if (window.fdWidget) {
        try {
          window.fdWidget.destroy();
        } catch (err) {
          logger.warn("[Freshchat] Error destroying widget on unmount", err);
        }
      }
    };
  }, []);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <FreshchatContext.Provider
      value={{
        enabled: ENABLED,
        ready,
        error,
        consentGranted,
        grantConsent,
        revokeConsent,
        open,
        close,
        showLauncher,
        hideLauncher,
      }}
    >
      {children}
    </FreshchatContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useFreshchat = (): FreshchatContextType => {
  const context = useContext(FreshchatContext);
  if (context === undefined) {
    return disabledFreshchatContext;
  }
  return context;
};
